import PropTypes from 'prop-types';
import React from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-dropdown-select';

/**
 * MessageValidityPeriod component allows users to set a custom validity period for utility and authentication templates.
 * The validity period determines how long WhatsApp will attempt to deliver the message before it expires.
 */
const MessageValidityPeriod = ({ validityPeriod, setValidityPeriod, setCodeExp }) => {
    const [customValidity, setCustomValidity] = React.useState(validityPeriod !== 10);

    // Options for the validity period dropdown
    const validityOptions = [
        { value: 10, label: '10 minutes' },
        { value: 30, label: '30 minutes' },
        { value: 60, label: '1 hour' },
        { value: 120, label: '2 hours' },
        { value: 720, label: '12 hours' },
        { value: 1440, label: '24 hours' },
    ];

    // Format the validity period for display
    const formatValidityPeriod = (minutes) => {
        if (minutes === 60) return '1 hour';
        if (minutes === 120) return '2 hours';
        if (minutes === 720) return '12 hours';
        if (minutes === 1440) return '24 hours';
        return `${minutes} minutes`;
    };

    // Handle changes to the validity period
    const handleValidityPeriodChange = (values) => {
        if (values && values.length > 0) {
            const newValue = values[0].value;
            setValidityPeriod(newValue);

            // For authentication templates, also update the code expiration time
            if (setCodeExp) {
                setCodeExp(newValue);
            }
        }
    };

    return (
        <div className="validity-section">
            <div className="d-flex align-items-center mb-2">
                <p className="mb-0" style={{ fontSize: '1rem' }}>Message Validity Period</p>
            </div>

            <p className="text-muted small mb-3">
                You can set a custom validity period that your message must be delivered
                by before it expires. If a message is not delivered within this timeframe, you
                will not be charged and your customer will not see the message.
            </p>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Label htmlFor="custom-validity-switch" className="mb-0">
                    <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>Set custom validity period for your message</span>
                </Form.Label>
                <Form.Check
                    type="switch"
                    id="custom-validity-switch"
                    checked={customValidity}
                    onChange={(e) => {
                        setCustomValidity(e.target.checked);
                        if (!e.target.checked) {
                            setValidityPeriod(10);
                            if (setCodeExp) {
                                setCodeExp(10);
                            }
                        }
                    }}
                    label=""
                />
            </div>

            {customValidity ? (
                <Form.Group className="mb-3">
                    <Form.Label>Validity period</Form.Label>
                    <Select
                        values={[
                            {
                                value: validityPeriod,
                                label: formatValidityPeriod(validityPeriod),
                            },
                        ]}
                        onChange={handleValidityPeriodChange}
                        options={validityOptions}
                        placeholder="Select validity period"
                        className="validity-period-select"
                    />
                </Form.Group>
            ) : (
                <p className="text-muted small">
                    If you don't set a custom validity period, the standard 10 minutes WhatsApp
                    message validity period will be applied.
                </p>
            )}
        </div>
    );
};

MessageValidityPeriod.propTypes = {
    validityPeriod: PropTypes.number.isRequired,
    setValidityPeriod: PropTypes.func.isRequired,
    setCodeExp: PropTypes.func,
};

export default MessageValidityPeriod; 